import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  subscribeToLiveClass,
  unsubscribeFromLiveClass,
  subscribeToSubject,
  unsubscribeFromSubject,
  subscribeToGrade,
  unsubscribeFromGrade,
} from "../../../services/notificationService";

/**
 * Custom hook for managing notification subscription mutations
 * This hook provides mutation functions for subscribing and unsubscribing
 * from various notification types (session, subject, grade)
 */
export const useNotificationMutations = () => {
  const queryClient = useQueryClient();

  // Helper to invalidate subscription status queries
  const invalidateSubscriptionQueries = (email: string) => {
    queryClient.invalidateQueries({
      queryKey: ["subscriptionStatus", email],
    });
  };

  // Subscribe to specific live class
  const subscribeToClass = useMutation({
    mutationFn: async ({
      email,
      sessionId,
      grade,
      userId,
      name,
    }: {
      email: string;
      sessionId: string;
      grade: string;
      userId: string;
      name?: string;
    }) => {
      return subscribeToLiveClass(email, sessionId, grade, {
        userId,
        name,
      });
    },
    onMutate: async (variables) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: ["subscriptionStatus", variables.email, variables.sessionId],
      });

      // Save the previous value
      const previousData = queryClient.getQueryData([
        "subscriptionStatus",
        variables.email,
        variables.sessionId,
      ]);

      // Optimistically update
      queryClient.setQueryData(
        ["subscriptionStatus", variables.email, variables.sessionId],
        (old: any) => ({
          ...(old ?? {}),
          isSubscribed: true,
          subscriptionType: "session",
          // Update other fields accordingly
        })
      );

      return { previousData };
    },
    // Handle errors by reverting to previous state
    onError: (err, variables, context) => {
      queryClient.setQueryData(
        ["subscriptionStatus", variables.email, variables.sessionId],
        context.previousData
      );
    },
    onSuccess: (data, variables) => {
      invalidateSubscriptionQueries(variables.email);
    },
  });

  // Unsubscribe from specific live class
  const unsubscribeFromClass = useMutation({
    mutationFn: async ({
      email,
      sessionId,
      grade,
      userId,
      name,
    }: {
      email: string;
      sessionId: string;
      grade: string;
      userId: string;
      name?: string;
    }) => {
      return unsubscribeFromLiveClass(email, sessionId, grade, {
        userId,
        name,
      });
    },
    onSuccess: (data, variables) => {
      invalidateSubscriptionQueries(variables.email);
    },
  });

  // Subscribe to all classes for a subject
  const subscribeToSubjectClasses = useMutation({
    mutationFn: async ({
      email,
      subject,
      grade,
      userId,
      name,
    }: {
      email: string;
      subject: string;
      grade: string;
      userId: string;
      name?: string;
    }) => {
      return subscribeToSubject(email, subject, grade, {
        userId,
        name,
      });
    },
    onSuccess: (data, variables) => {
      invalidateSubscriptionQueries(variables.email);
    },
  });

  // Unsubscribe from all classes for a subject
  const unsubscribeFromSubjectClasses = useMutation({
    mutationFn: async ({
      email,
      subject,
      grade,
      userId,
      name,
    }: {
      email: string;
      subject: string;
      grade: string;
      userId: string;
      name?: string;
    }) => {
      return unsubscribeFromSubject(email, subject, grade, {
        userId,
        name,
      });
    },
    onSuccess: (data, variables) => {
      invalidateSubscriptionQueries(variables.email);
    },
  });

  // Subscribe to all classes for a grade
  const subscribeToGradeClasses = useMutation({
    mutationFn: async ({
      email,
      grade,
      userId,
      name,
    }: {
      email: string;
      grade: string;
      userId: string;
      name?: string;
    }) => {
      return subscribeToGrade(email, grade, {
        userId,
        name,
      });
    },
    onSuccess: (data, variables) => {
      invalidateSubscriptionQueries(variables.email);
    },
  });

  // Unsubscribe from all classes for a grade
  const unsubscribeFromGradeClasses = useMutation({
    mutationFn: async ({
      email,
      grade,
      userId,
      name,
    }: {
      email: string;
      grade: string;
      userId: string;
      name?: string;
    }) => {
      return unsubscribeFromGrade(email, grade, {
        userId,
        name,
      });
    },
    onSuccess: (data, variables) => {
      invalidateSubscriptionQueries(variables.email);
    },
  });

  return {
    subscribeToClass,
    unsubscribeFromClass,
    subscribeToSubjectClasses,
    unsubscribeFromSubjectClasses,
    subscribeToGradeClasses,
    unsubscribeFromGradeClasses,
  };
};
